import React, { useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Box, Button, Card, CardContent, Chip, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import {
  getStorePayouts,
  getStorePayoutHistory,
  sendStorePayout,
} from "../../api/deliveryCompanyService";
import { formatCurrency, formatDate } from "../Admin/constants";

const VIEW_TABS = [
  { value: "requests", label: "Payout Requests" },
  { value: "awaiting", label: "Sent - Awaiting Confirmation" },
  { value: "history", label: "History" },
];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function StoreFees() {
  const [view, setView] = useState("requests");
  const [rows, setRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyStoreId, setBusyStoreId] = useState(null);

  function loadRows() {
    setLoading(true);
    Promise.all([getStorePayouts(), getStorePayoutHistory()])
      .then(([payouts, history]) => {
        setRows(payouts);
        setHistoryRows(history);
        setError("");
      })
      .catch(() => setError("Could not load store payouts. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(loadRows, []);

  async function handleSend(row) {
    setBusyStoreId(row.storeId);
    try {
      await sendStorePayout(row.storeId);
      loadRows();
    } catch {
      setError("Could not send this payout.");
    } finally {
      setBusyStoreId(null);
    }
  }

  const requestRows = useMemo(
    () => rows.filter((r) => r.requestedAmount > 0 || r.pendingAmount > 0),
    [rows]
  );

  // StorePayoutStatus: Sent = 1, Received = 2 (serialized as the numeric enum value)
  const awaitingRows = useMemo(() => historyRows.filter((r) => r.status === 1), [historyRows]);
  const completedRows = useMemo(() => historyRows.filter((r) => r.status === 2), [historyRows]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          requested: acc.requested + (r.requestedAmount || 0),
          newPending: acc.newPending + (r.pendingAmount || 0),
          awaitingConfirmation: acc.awaitingConfirmation + (r.awaitingConfirmationAmount || 0),
          totalPaid: acc.totalPaid + (r.totalPaid || 0),
          discountGiven: acc.discountGiven + (r.requestedDiscountAmount || 0) + (r.pendingDiscountAmount || 0),
        }),
        { requested: 0, newPending: 0, awaitingConfirmation: 0, totalPaid: 0, discountGiven: 0 }
      ),
    [rows]
  );

  const requestColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 180 },
      {
        field: "hasOpenRequest",
        headerName: "Requested By Owner",
        width: 190,
        sortable: false,
        renderCell: (params) =>
          params.row.hasOpenRequest ? (
            <Box sx={{ py: 1 }}>
              <Chip size="small" color="warning" label="Requested" />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {formatDate(params.row.requestedAt)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Not requested yet
            </Typography>
          ),
      },
      {
        field: "requestedAmount",
        headerName: "Requested Amount",
        width: 160,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "requestedOrderCount", headerName: "Orders", width: 90 },
      {
        field: "requestedDiscountAmount",
        headerName: "Coupon Discount",
        width: 150,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      {
        field: "pendingAmount",
        headerName: "New (Not Yet Requested)",
        width: 190,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "pendingOrderCount", headerName: "Orders", width: 90 },
      {
        field: "pendingDiscountAmount",
        headerName: "Coupon Discount",
        width: 150,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      {
        field: "send",
        headerName: "",
        width: 190,
        sortable: false,
        renderCell: (params) => {
          const label = params.row.hasOpenRequest ? "Send Requested Payout" : "Send Payout";
          return (
            <Button
              size="small"
              variant="contained"
              disabled={busyStoreId === params.row.storeId}
              onClick={() => handleSend(params.row)}
            >
              {label}
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyStoreId]
  );

  const awaitingColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: "Status",
        width: 190,
        sortable: false,
        renderCell: () => <Chip size="small" color="info" label="Sent - Awaiting Confirmation" />,
      },
      { field: "orderCount", headerName: "Orders", width: 90 },
      { field: "amount", headerName: "Amount", width: 140, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "discountAmount",
        headerName: "Coupon Discount",
        width: 150,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "sentAt", headerName: "Sent At", width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "note", headerName: "Note", flex: 1, minWidth: 150 },
    ],
    []
  );

  const historyColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        sortable: false,
        renderCell: () => <Chip size="small" color="success" label="Received" />,
      },
      { field: "orderCount", headerName: "Orders", width: 90 },
      { field: "amount", headerName: "Amount", width: 140, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "discountAmount",
        headerName: "Coupon Discount",
        width: 150,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "sentAt", headerName: "Sent At", width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "receivedAt", headerName: "Confirmed At", width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "note", headerName: "Note", flex: 1, minWidth: 150 },
    ],
    []
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Store Fees
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        On cash-on-delivery orders your drivers deliver, your driver collects the full amount from
        the customer - so beyond what you owe the platform, you separately owe each store its net
        product revenue (subtotal minus the store's own coupon discount, minus the platform's 5%
        fee). When a store owner explicitly requests their money, it's locked in below as
        "Requested" - any new order confirmed afterwards starts a fresh balance instead of mixing
        into that request. Send the requested (or newly accrued) amount, and the store confirms
        receipt from their own side.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={HourglassTopOutlinedIcon}
            label="Requested - Awaiting Send"
            value={formatCurrency(totals.requested)}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={AddCircleOutlineOutlinedIcon}
            label="New - Not Yet Requested"
            value={formatCurrency(totals.newPending)}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={MarkEmailReadOutlinedIcon}
            label="Sent - Awaiting Confirmation"
            value={formatCurrency(totals.awaitingConfirmation)}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={PaidOutlinedIcon}
            label="Total Paid All-Time"
            value={formatCurrency(totals.totalPaid)}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={LocalOfferOutlinedIcon}
            label="Coupon Discounts Owed (Stores)"
            value={formatCurrency(totals.discountGiven)}
            color="error"
          />
        </Grid>
      </Grid>

      <Tabs value={view} onChange={(e, v) => setView(v)} sx={{ mb: 2 }}>
        {VIEW_TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {view === "requests" && (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          <DataGrid
            autoHeight
            rows={requestRows}
            getRowId={(row) => row.storeId}
            columns={requestColumns}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            sx={{ border: "none" }}
          />
          {!loading && requestRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              Nothing owed to any store right now.
            </Typography>
          )}
        </Box>
      )}

      {view === "awaiting" && (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          <DataGrid
            autoHeight
            rows={awaitingRows}
            getRowId={(row) => row.storePayoutId}
            columns={awaitingColumns}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            sx={{ border: "none" }}
          />
          {!loading && awaitingRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              Nothing sent and awaiting confirmation right now.
            </Typography>
          )}
        </Box>
      )}

      {view === "history" && (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          <DataGrid
            autoHeight
            rows={completedRows}
            getRowId={(row) => row.storePayoutId}
            columns={historyColumns}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            sx={{ border: "none" }}
          />
          {!loading && completedRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              No confirmed payouts yet.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
